import { Brain, ArrowRight, TrendingUp, Shield, Scale, Target } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScoreCard {
  label: string;
  score: number;
  description: string;
  icon: React.ElementType;
}

interface Recommendation {
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  category: string;
}

interface Benchmark {
  metric: string;
  your_score: number;
  industry_avg: number;
  top_quartile: number;
}

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

const SCORE_CARDS: ScoreCard[] = [
  { label: 'AI Readiness Score', score: 72, description: 'Organizational readiness for AI adoption and governance', icon: Target },
  { label: 'Governance Maturity', score: 65, description: 'Maturity of AI governance processes and controls', icon: Shield },
  { label: 'Risk Posture', score: 78, description: 'Overall risk management effectiveness for AI systems', icon: TrendingUp },
  { label: 'Compliance Coverage', score: 81, description: 'Coverage across applicable compliance frameworks', icon: Scale },
];

const RECOMMENDATIONS: Recommendation[] = [
  { title: 'Establish a cross-functional AI Ethics Board', description: 'Create a dedicated committee with representatives from legal, engineering, compliance, and business to oversee AI governance decisions.', priority: 'high', category: 'Governance' },
  { title: 'Implement automated model monitoring', description: 'Deploy continuous monitoring for model drift, bias, and performance degradation across all production AI systems.', priority: 'high', category: 'Operations' },
  { title: 'Complete EU AI Act risk classification', description: 'Classify all AI systems according to EU AI Act risk tiers and document compliance requirements for each.', priority: 'high', category: 'Compliance' },
  { title: 'Develop AI incident response playbook', description: 'Create documented procedures for responding to AI-related incidents including bias detection, model failures, and data breaches.', priority: 'medium', category: 'Risk' },
  { title: 'Establish data lineage tracking', description: 'Implement end-to-end data lineage for all training datasets to ensure traceability and reproducibility.', priority: 'medium', category: 'Data' },
  { title: 'Conduct quarterly bias audits', description: 'Schedule regular bias audits across all customer-facing AI models with documented remediation plans.', priority: 'medium', category: 'Fairness' },
  { title: 'Build AI skills training program', description: 'Develop mandatory training for all employees who interact with AI systems, covering responsible use and governance policies.', priority: 'low', category: 'Education' },
  { title: 'Create model documentation standards', description: 'Define and enforce model card standards for all AI models including purpose, limitations, and ethical considerations.', priority: 'low', category: 'Documentation' },
];

const BENCHMARKS: Benchmark[] = [
  { metric: 'Policy Coverage', your_score: 78, industry_avg: 62, top_quartile: 89 },
  { metric: 'Risk Assessment Frequency', your_score: 65, industry_avg: 48, top_quartile: 82 },
  { metric: 'Model Documentation', your_score: 71, industry_avg: 55, top_quartile: 91 },
  { metric: 'Incident Response Time', your_score: 82, industry_avg: 58, top_quartile: 88 },
  { metric: 'Compliance Automation', your_score: 60, industry_avg: 42, top_quartile: 78 },
  { metric: 'Shadow AI Detection', your_score: 55, industry_avg: 35, top_quartile: 72 },
];

const QUICK_LINKS = [
  { label: 'AI Model Registry', href: '/governance/models', description: 'Manage registered AI models' },
  { label: 'Compliance Evidence', href: '/governance/evidence', description: 'Upload and review evidence' },
  { label: 'Cost Tracking', href: '/governance/costs', description: 'Monitor AI spending' },
  { label: 'Deployments', href: '/governance/deployments', description: 'Manage model deployments' },
  { label: 'Asset Registry', href: '/governance/assets', description: 'Catalog AI assets' },
  { label: 'Governance Scanner', href: '/governance/scanner', description: 'Run compliance scans' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-500';
  if (score >= 60) return 'text-yellow-500';
  return 'text-red-500';
}

function scoreBgColor(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
}

function priorityBadge(priority: 'high' | 'medium' | 'low') {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (priority) {
    case 'high':
      return <span className={`${base} bg-red-500/15 text-red-500`}>High</span>;
    case 'medium':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Medium</span>;
    case 'low':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Low</span>;
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function IntelligencePage() {
  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Brain className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Platform Intelligence</h1>
          <p className="text-muted-foreground mt-1">AI governance insights, recommendations, and benchmarks for your organization.</p>
        </div>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SCORE_CARDS.map((card) => (
          <div key={card.label} className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <card.icon className="h-4 w-4 text-primary" />
              <p className="text-sm text-muted-foreground">{card.label}</p>
            </div>
            <p className={`text-3xl font-bold ${scoreColor(card.score)}`}>{card.score}<span className="text-sm text-muted-foreground font-normal">/100</span></p>
            <p className="text-xs text-muted-foreground mt-1">{card.description}</p>
          </div>
        ))}
      </div>

      {/* Recommendations */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h2 className="text-lg font-semibold text-foreground mb-4">Recommendations</h2>
        <div className="space-y-3">
          {RECOMMENDATIONS.map((rec) => (
            <div key={rec.title} className="flex items-start gap-4 px-4 py-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="text-sm font-medium text-foreground">{rec.title}</h3>
                  {priorityBadge(rec.priority)}
                  <span className="px-2 py-0.5 rounded-full text-xs bg-accent text-accent-foreground">{rec.category}</span>
                </div>
                <p className="text-sm text-muted-foreground">{rec.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benchmark comparisons */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h2 className="text-lg font-semibold text-foreground mb-4">Industry Benchmarks</h2>
        <div className="space-y-4">
          {BENCHMARKS.map((b) => (
            <div key={b.metric}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">{b.metric}</span>
                <span className={`text-sm font-bold ${scoreColor(b.your_score)}`}>{b.your_score}</span>
              </div>
              <div className="relative h-3 rounded-full bg-muted overflow-hidden">
                {/* Industry average marker */}
                <div
                  className="absolute top-0 h-full w-0.5 bg-muted-foreground/50 z-10"
                  style={{ left: `${b.industry_avg}%` }}
                  title={`Industry Avg: ${b.industry_avg}`}
                />
                {/* Top quartile marker */}
                <div
                  className="absolute top-0 h-full w-0.5 bg-green-500/50 z-10"
                  style={{ left: `${b.top_quartile}%` }}
                  title={`Top Quartile: ${b.top_quartile}`}
                />
                {/* Your score bar */}
                <div
                  className={`h-full rounded-full transition-all ${scoreBgColor(b.your_score)}/80`}
                  style={{ width: `${b.your_score}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-muted-foreground">Industry Avg: {b.industry_avg}</span>
                <span className="text-xs text-green-500">Top Quartile: {b.top_quartile}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Governance Sections</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {QUICK_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="rounded-lg border border-border bg-card p-5 hover:border-primary/50 transition-colors group flex items-center justify-between"
            >
              <div>
                <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">{link.label}</h3>
                <p className="text-sm text-muted-foreground mt-1">{link.description}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
